import { useModal } from '@ebay/nice-modal-react'
import { Link, useSearch } from '@tanstack/router'
import type { PropsWithChildren } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

import useDimensions from '../../hooks/useDimensions'
import { useBoundStore } from '../../store/store'
import type { Game } from '../../types/game'
import { PendingDepositModal } from '../payment-form'
import CasinoSlider from './casino-slider'
import CompetitionsSlider from './competitions-slider'
import PromotionsSlider from './promotions-slider'
import SportsSlider from './sports-slider'

type CategoryProps = {
  title: string
  subtitle: string
  linkText: string
  route: '/sports' | '/casino/lobby' | '/sorteos' | '/promotions' | undefined
  onRouteClick?: () => void
}

const Category = ({
  title,
  subtitle,
  route,
  onRouteClick,
  children,
  linkText,
}: PropsWithChildren<CategoryProps>) => {
  const { isDesktop } = useDimensions()

  return (
    <div className="sticky z-[1] mt-6 xl:max-w-screen-xl xl:mt-6 xl:mb-0 xl:mx-auto">
      <div className="xl:flex xl:items-end xl:justify-between">
        <div>
          <h2 className="text-3xl font-Geometr706-BlkCn-BT text-[#0e091e] uppercase px-3 py-0">
            {title}
          </h2>
          <h2 className="text-[15px] text-[#848292] mt-3 mb-6 px-3 py-0">
            {subtitle}
          </h2>
        </div>
        {isDesktop && (
          <div className="flex justify-end mt-3 px-3 py-0 xl:mt-0 xl:mb-6">
            <Link
              className="text-xs font-Syncopate-Bold text-[#0e091e] uppercase xl:text-[15px]"
              to={route}
              onClick={onRouteClick}>
              {linkText}
            </Link>
          </div>
        )}
      </div>
      <div className="px-2 py-0">{children}</div>
      {!isDesktop && (
        <div className="flex justify-end mt-3 px-3 py-0 xl:mt-0 xl:mb-6">
          <Link
            className="text-xs font-Syncopate-Bold text-[#0e091e] uppercase xl:text-[15px]"
            to={route}>
            {linkText}
          </Link>
        </div>
      )}
    </div>
  )
}

interface CategoriesProps {
  slots?: Game[]
  page?: '' | 'casinoads' | 'sportsads' | 'sorteoads'
}

const Categories = ({ slots, page = '' }: CategoriesProps) => {
  const pending_bind = useBoundStore(state => state.user.pending_bind)
  const pendingDepositModal = useModal(PendingDepositModal)
  const { t } = useTranslation(['drawer', 'homeV2'])
  const { bonus } = useSearch({ from: `/${page}` })

  const isDepositPending = +pending_bind === 0 || +pending_bind === 2

  return (
    <div className="relative bg-[#f2f2fa] pb-[76px] xl:pb-[100px]">
      <div className="absolute bg-chips bg-center bg-[1000px_auto] bottom-[-85px] w-full h-[175px] left-0 xl:bg-[1440px_auto] xl:bottom-[-121px] xl:h-[250px]" />
      <div className="relative w-full h-[26px] mb-4 xl:mb-9">
        <div
          className="absolute w-full h-full bg-category-crown bg-center"
          style={{ backgroundSize: '1440px 26px' }}
        />
      </div>
      <LazyLoadComponent visibleByDefault={!!bonus}>
        {page === 'casinoads' ? (
          <Category
            title={t('slot-machines', { ns: 'homeV2' })}
            subtitle={t('more-than-1000', { ns: 'homeV2' })}
            route="/casino/lobby"
            linkText={t('more-games', { ns: 'homeV2' })}>
            <CasinoSlider slots={slots ?? []} />
          </Category>
        ) : page === 'sportsads' ? (
          <Category
            title={t('betting-sports', { ns: 'homeV2' })}
            subtitle={t('bet-on-favorite', { ns: 'homeV2' })}
            route={isDepositPending ? undefined : '/sports'}
            onRouteClick={() => isDepositPending && pendingDepositModal.show()}
            linkText={t('more-sports', { ns: 'homeV2' })}>
            <SportsSlider />
          </Category>
        ) : page === 'sorteoads' ? (
          <Category
            title={t('raffles', { ns: 'drawer' })}
            subtitle={t('buy-tickets', { ns: 'homeV2' })}
            route={isDepositPending ? undefined : '/sorteos'}
            onRouteClick={() => isDepositPending && pendingDepositModal.show()}
            linkText={t('more-raffles', { ns: 'homeV2' })}>
            <CompetitionsSlider />
          </Category>
        ) : (
          <>
            <Category
              title={t('betting-sports', { ns: 'homeV2' })}
              subtitle={t('bet-on-favorite', { ns: 'homeV2' })}
              route={isDepositPending ? undefined : '/sports'}
              onRouteClick={() =>
                isDepositPending && pendingDepositModal.show()
              }
              linkText={t('more-sports', { ns: 'homeV2' })}>
              <SportsSlider />
            </Category>
            <Category
              title={t('slot-machines', { ns: 'homeV2' })}
              subtitle={t('more-than-1000', { ns: 'homeV2' })}
              route="/casino/lobby"
              linkText={t('more-games', { ns: 'homeV2' })}>
              <CasinoSlider slots={slots ?? []} />
            </Category>
            <Category
              title={t('promotions', { ns: 'homeV2' })}
              subtitle={t('incredible-welcome', { ns: 'homeV2' })}
              route="/promotions"
              linkText={t('more-promotions', { ns: 'homeV2' })}>
              <PromotionsSlider />
            </Category>
          </>
        )}
      </LazyLoadComponent>
    </div>
  )
}

export default Categories
